import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { eur } from "@/lib/api";
import { Card, Btn } from "@/components/common";
import { MONTHS } from "./helpers";
import { Check, Undo2, Paperclip, ScanLine } from "lucide-react";
import ReceiptsModal from "./ReceiptsModal";

const TARGET = { bollette: "Bollette", lavori: "Lavori", ristorante: "Ristorante", spese_generali: "Spese generali", affitti: "Affitti", staff: "Staff" };
const label = (ym) => `${MONTHS[Number(ym.slice(5)) - 1]} ${ym.slice(0, 4)}`;

export default function SpeseDaConfermare({ onChanged }) {
  const [month, setMonth] = useState(null);
  const [d, setD] = useState(null);
  const [amounts, setAmounts] = useState({});
  const [busy, setBusy] = useState(false);
  const [receipts, setReceipts] = useState(null);
  const [scans, setScans] = useState({});
  const [scanning, setScanning] = useState(null);
  const fileRef = useRef(null);
  const scanFor = useRef(null);

  const pickScan = (id) => { scanFor.current = id; fileRef.current?.click(); };
  const onScanFile = async (file) => {
    const id = scanFor.current;
    if (fileRef.current) fileRef.current.value = "";
    if (!file || !id) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File troppo grande (max 10 MB)"); return; }
    setScanning(id);
    try {
      const b64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
      const { data } = await api.post("/receipts/scan", { image_base64: b64 });
      setScans((s) => ({ ...s, [id]: { ...data, file } }));
      if (data.amount != null) setAmounts((a) => ({ ...a, [id]: data.amount }));
      toast[data.amount != null ? "success" : "warning"](data.amount != null ? "Importo letto dalla ricevuta: controllalo e conferma" : "Importo non leggibile: inseriscilo a mano (la foto verrà comunque allegata)");
    } catch (e) { toast.error(e.response?.data?.detail || "Lettura non riuscita"); }
    setScanning(null);
  };

  const load = useCallback(async (ym) => {
    try {
      const r = await api.get("/spese-da-confermare", { params: ym ? { month: ym } : {} });
      setD(r.data); setMonth(r.data.month);
      setScans((s) => (Object.keys(s).length && ym !== r.data.month ? {} : s));
      setAmounts(Object.fromEntries(r.data.items.filter((i) => i.status === "da_confermare").map((i) => [i.id, i.amount])));
    } catch (e) { toast.error(e.response?.data?.detail || "Caricamento non riuscito"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const confirm = async (ids) => {
    if (ids.some((id) => amounts[id] === "" || amounts[id] == null)) { toast.error("Inserisci l'importo di ogni voce da confermare"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/spese-da-confermare", { month, items: ids.map((id) => ({ id, amount: Number(amounts[id]) || 0, currency: scans[id]?.currency || undefined })) });
      let attached = 0;
      for (const id of ids) {
        const eid = data.expenses?.[id];
        if (!eid || !scans[id]?.file) continue;
        const fd = new FormData();
        fd.append("file", scans[id].file);
        try { await api.post(`/expenses/${eid}/attachments`, fd); attached += 1; } catch (e) { toast.error(`Foto di una voce non allegata: riprova dalla graffetta`); }
      }
      setScans((s) => Object.fromEntries(Object.entries(s).filter(([k]) => !ids.includes(k))));
      toast.success(`${data.created} spese registrate in Guesthouse${attached ? ` · ${attached} foto allegate` : ""}`);
      await load(month); onChanged?.();
    } catch (e) { toast.error("Conferma non riuscita"); }
    setBusy(false);
  };
  const undo = async (id) => {
    if (!window.confirm("Annullare la conferma? Anche gli allegati della spesa verranno rimossi.")) return;
    try {
      await api.delete(`/spese-da-confermare/${month}/${id}`);
      toast.success("Conferma annullata");
    } catch (e) { toast.error("Annullamento non riuscito"); }
    await load(month); onChanged?.();
  };

  if (!d) return null;
  const pending = d.items.filter((i) => i.status === "da_confermare");
  return (
    <Card className="p-4" data-testid="pv-confirm">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <div className="text-sm font-semibold text-slate-700">Spese da confermare</div>
          <div className="text-[11px] text-slate-400">Le voci confermate diventano spese reali in Guesthouse, con data il 1° del mese. Affitti e staff restano nelle loro pagine.</div>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="pv-confirm-month" value={month} onChange={(e) => load(e.target.value)} className="px-2 py-1.5 rounded-md border border-slate-300 text-sm">
            {d.months.map((ym) => <option key={ym} value={ym}>{label(ym)}</option>)}
          </select>
          <Btn data-testid="pv-confirm-all" disabled={!pending.length || busy} onClick={() => confirm(pending.map((i) => i.id))}><Check size={15} /> Conferma tutte ({pending.length})</Btn>
        </div>
      </div>
      {d.closed && <div className="text-xs text-rose-600 mb-2">Mese di chiusura: le voci variabili non sono richieste.</div>}
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="pv-confirm-table">
          <tbody>
            {d.items.map((it) => (
              <tr key={it.id} className="border-b border-slate-100 flex flex-wrap items-center gap-x-2 py-1.5 sm:table-row sm:py-0" data-testid={`pv-confirm-row-${it.id}`}>
                <td className="sm:py-1.5 pr-2 text-slate-400 whitespace-nowrap">{it.group}</td>
                <td className="sm:py-1.5 pr-2 font-medium sm:font-normal">{it.name}</td>
                <td className="sm:py-1.5 pr-2 text-slate-400">{TARGET[it.target]}</td>
                <td className="sm:py-1.5 pr-2 sm:text-right whitespace-nowrap">
                  {it.status === "da_confermare"
                    ? <input data-testid={`pv-confirm-amount-${it.id}`} type="number" value={amounts[it.id] ?? ""} onChange={(e) => setAmounts({ ...amounts, [it.id]: e.target.value })} className="w-24 text-right px-2 py-1 rounded border border-slate-300" />
                    : <span className="tabular">{Number(it.amount).toLocaleString("it-IT", { useGrouping: "always" })}</span>} <span className="text-slate-500">{it.status === "da_confermare" && scans[it.id]?.currency ? scans[it.id].currency : it.currency}</span>
                  {scans[it.id] && <ScanInfo s={scans[it.id]} month={month} itemCurrency={it.currency} />}
                </td>
                <td className="sm:py-1.5 ml-auto sm:text-right whitespace-nowrap">
                  {it.status === "da_confermare" && <button data-testid={`pv-scan-${it.id}`} title="Leggi importo dalla foto della ricevuta" disabled={scanning === it.id} onClick={() => pickScan(it.id)} className="mr-1 p-1.5 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-sky-700 rounded border border-slate-200"><ScanLine size={14} />{scanning === it.id ? "Lettura…" : "Foto"}</button>}
                  {it.status === "da_confermare" && <button data-testid={`pv-confirm-btn-${it.id}`} disabled={busy} onClick={() => confirm([it.id])} className="text-xs font-semibold text-sky-700 hover:text-sky-900 px-2 py-1 rounded border border-sky-200">Conferma</button>}
                  {it.status === "confermata" && <span className="inline-flex items-center gap-2"><span className="text-emerald-700 font-semibold">Confermata · {eur(it.eur)}</span><button data-testid={`pv-receipts-${it.id}`} title="Foto ricevuta" onClick={() => setReceipts(it)} className={`p-1.5 inline-flex items-center gap-0.5 rounded ${it.attachments ? "text-sky-700" : "text-slate-400 hover:text-sky-600"}`}><Paperclip size={16} />{it.attachments > 0 && <span className="text-[11px] font-semibold">{it.attachments}</span>}</button><button data-testid={`pv-confirm-undo-${it.id}`} title="Annulla" onClick={() => undo(it.id)} className="p-1.5 text-slate-400 hover:text-rose-600"><Undo2 size={16} /></button></span>}
                  {it.status === "managed" && <span className="text-slate-400">in pagina {TARGET[it.target]}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[11px] text-slate-500 mt-2" data-testid="pv-confirm-managed">Registrato in Affitti questo mese: <b>{eur(d.managed.affitti)}</b> · in Staff: <b>{eur(d.managed.staff)}</b> · Allega la foto della ricevuta con la graffetta sulle voci confermate.</div>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf" className="hidden" data-testid="pv-scan-input" onChange={(e) => onScanFile(e.target.files?.[0])} />
      <ReceiptsModal expense={receipts} onClose={() => setReceipts(null)} onChanged={() => load(month)} />
    </Card>
  );
}

function ScanInfo({ s, month, itemCurrency }) {
  const otherMonth = s.date && s.date.slice(0, 7) !== month;
  return (
    <div className="text-[10px] text-slate-500 mt-0.5" data-testid="pv-scan-info">
      Letto{s.vendor ? `: ${s.vendor}` : ""}{s.date ? ` · ${s.date.split("-").reverse().join("/")}` : ""}
      {otherMonth && <span className="text-amber-600 font-semibold"> · data di un altro mese</span>}
      {s.currency && s.currency !== itemCurrency && <span className="text-amber-600 font-semibold"> · valuta {s.currency}</span>}
    </div>
  );
}
